import { Modal } from "@circleco/compass/components/Modal";

import { t } from "@/i18n-js/instance";
import { useDeleteComment } from "@/react/components/CommentsCompass/data/useDeleteComment";
import {
  ModalManager,
  useModal,
} from "@/react/components/Layout/ModalManagerProvider";
import type { Comment } from "@/react/types/Comment";

interface DeleteModalProps {
  comment: Comment;
  onCommentDeleted?: (comment: Comment) => void;
}

const DeleteModal = ModalManager.create(
  ({ comment, onCommentDeleted }: DeleteModalProps) => {
    const modal = useModal();
    const { deleteComment, isPending } = useDeleteComment({
      comment,
      onSuccess: () => {
        onCommentDeleted?.(comment);
        void modal.hide();
      },
    });

    return (
      <Modal.Root
        open={modal.visible}
        onOpenChange={open => {
          if (!open) void modal.hide();
        }}
        onOpenChangeComplete={open => {
          if (!open) modal.remove();
        }}
      >
        <Modal.Content>
          <Modal.Header
            title={t("comments.delete.confirm.title")}
            showCloseButton
          />
          <Modal.Body>{t("comments.delete.confirm.description")}</Modal.Body>
          <Modal.Footer
            secondaryAction={{
              label: t("comments.delete.actions.cancel"),
              onClick: () => void modal.hide(),
            }}
            primaryAction={{
              type: "button",
              label: t("comments.delete.actions.delete"),
              variant: "destructive",
              loading: isPending,
              onClick: () => deleteComment(),
            }}
          />
        </Modal.Content>
      </Modal.Root>
    );
  },
);

export const useDeleteModal = () => useModal(DeleteModal);
